import { camelCase } from '../lib/camel-case'

import { LARGE_CAR_SERVICE, SAME_DAY_SERVICE, CONGESTION_ZONE_SERVICE } from './constants/prices'
import * as TYPES from './types'
import { slots } from './constants/slots'
import { GBP } from '../lib/gbp'
import { defaultBaseService } from './adapters'

const initialServices = {
  0: true
}

const initialBookingForm = {
  servicesAvailability: { ...initialServices },
  services: { ...initialServices },
  address: '',
  // The typed postcode, normalised to "N6 5UQ" by the field on blur. It used
  // to be read off Google's geocoder answer and then thrown away -
  // prepareOrderParams had no postcode key - so orders.postcode was only ever
  // whatever the server could scrape back out of the address text. It is now
  // a field the customer fills in, which is the one thing the 52 area pages
  // and the operator's dispatch note both want.
  postcode: '',
  homeAddress: '',
  date: null,
  // Which answers the form chose for the customer (31 Oct 2026).
  assumed: { date: false, carSize: false },
  timeSlot: '',
  carModel: '',
  carColor: '',
  carRegistration: '',
  notes: '',
  agentTip: false
}

export const initialState = {
  ...initialBookingForm,
  prices: { ids: [], byId: {}, byName: {}, byOrder: [] },
  // 'idle' | 'loading' | 'ready' | 'failed'. The marketing pages derive their
  // add-on LIST from /api/v2/services, not only their prices, so they need to
  // tell "not back yet" from "came back empty" from "did not come back".
  servicesStatus: 'idle',
  isSignIn: false,
  user: null,
  orders: [],
  message: null,
  messageTimeout: null,
  messagePosition: null,
  messageColor: null,
  promoCode: null,
  promoCodeInfo: null,
  // The last code refused, { code, reason, message } (lib/promo-credit.js
  // promoRefusal): said under the code field, cleared once the code is
  // edited, removed or accepted.
  promoCodeRefusal: null,
  // Pence of account credit the server will apply to this booking
  // (lib/credit-preview.js); 0 until it says otherwise.
  creditPreviewCents: 0,
  isCardComplete: false,
  isProcessingStripe: false,
  isStripeError: false,
  stripeError: '',
  isTermsAccepted: false,
  config: {
    ALLOW_BOOKING_IN_DAYS: 0
  }
}

export const reducer = (state = initialState, action) => {
  const {
    type,
    services,
    servicesAvailability,
    address,
    postcode,
    date,
    assumed,
    timeSlot,
    homeAddress,
    carModel,
    carRegistration,
    carColor,
    notes,
    agentTip,
    largeCar,
    sameDay,
    congestionZone,
    apiServices,
    user,
    orders,
    message,
    messageTimeout,
    messagePosition,
    messageColor,
    promoCode,
    promoCodeInfo,
    promoCodeRefusal,
    creditPreviewCents,
    credit,
    isCardComplete,
    isProcessingStripe,
    isStripeError,
    stripeError,
    isTermsAccepted,
    config
  } = action

  switch (type) {
    case TYPES.SET_SERVICES:
      // Today picked before the list loaded: SET_ANYTIME_TODAY had no id to
      // set, and this reset would leave "Anytime Today" with no surcharge.
      // The server adds it anyway (OrdersController#additional_params); this
      // keeps the price on screen honest.
      if (state.timeSlot === slots.today && SAME_DAY_SERVICE.id !== undefined) {
        return { ...state, services: { ...services, [SAME_DAY_SERVICE.id]: true }, servicesAvailability }
      }
      return { ...state, services, servicesAvailability }
    case TYPES.SET_ADDRESS:
      return { ...state, address }
    case TYPES.SET_POSTCODE:
      return { ...state, postcode }
    case TYPES.SET_DATE:
      return { ...state, date }
    case TYPES.SET_ASSUMED:
      return { ...state, assumed: { ...state.assumed, ...assumed } }
    case TYPES.SET_TIME_SLOT:
      return { ...state, timeSlot }
    case TYPES.SET_HOME_ADDRESS:
      return { ...state, homeAddress }
    case TYPES.SET_CAR_MODEL:
      return { ...state, carModel }
    case TYPES.SET_CAR_REGISTRATION:
      return { ...state, carRegistration }
    case TYPES.SET_CAR_COLOR:
      return { ...state, carColor }
    case TYPES.SET_NOTES:
      return { ...state, notes }
    case TYPES.SET_AGENT_TIP:
      return { ...state, agentTip }
    // Before the list has loaded the three surcharge ids are undefined: set
    // nothing, rather than one shared services["undefined"] that the price
    // on the events read as the large car (lib/price-lines.js priceProps).
    // SET_SERVICES from the list starts the large car at null anyway.
    case TYPES.SET_LARGE_CAR:
      if (LARGE_CAR_SERVICE.id === undefined) return state
      return { ...state, services: { ...state.services, [LARGE_CAR_SERVICE.id]: largeCar } }
    case TYPES.SET_ANYTIME_TODAY:
      if (SAME_DAY_SERVICE.id === undefined) return state
      return { ...state, services: { ...state.services, [SAME_DAY_SERVICE.id]: sameDay } }
    case TYPES.SET_CONGESTION_ZONE:
      if (CONGESTION_ZONE_SERVICE.id === undefined) return state
      return { ...state, services: { ...state.services, [CONGESTION_ZONE_SERVICE.id]: congestionZone } }
    case TYPES.SET_SERVICE_PRICES: {
      const ids = apiServices.map(service => service.id)
      const byName = apiServices.reduce((acc, service) => ({ ...acc, [camelCase(service.name)]: service }), {})
      const byId = apiServices.reduce((acc, service) => ({ ...acc, [service.id]: service }), {})
      const byOrder = [...apiServices]

      // `servicesStatus` goes to 'ready' here and not on
      // API_SERVICES_FETCH_ALL_SUCCESS, which is dispatched one line earlier
      // in use-services-api.js. React 16 does not batch across a promise, so
      // flipping it there would give the marketing pages one render saying
      // "ready" with no services in it, which is the empty-section state they
      // are specifically built to never show.
      return { ...state, prices: { ids, byId, byName, byOrder }, servicesStatus: 'ready' }
    }
    case TYPES.API_SERVICES_FETCH_ALL_START:
      return { ...state, servicesStatus: 'loading' }
    // /services derives its add-on list from this fetch, not only its prices,
    // so a failure has to be visible rather than silent: an empty section with
    // a heading over it tells a visitor we sell nothing. pages/services/extras
    // reads this and says what happened instead.
    case TYPES.API_SERVICES_FETCH_ALL_FAILURE:
      return { ...state, servicesStatus: 'failed' }
    case TYPES.TOGGLE_SIGN_IN: {
      return { ...state, isSignIn: !state.isSignIn }
    }
    case TYPES.SET_USER:
      return { ...state, user }
    case TYPES.SIGN_OUT:
      return {
        ...state,
        user: null,
        orders: [],
        address: '',
        postcode: '',
        homeAddress: '',
        date: null,
        timeSlot: '',
        carModel: '',
        carRegistration: '',
        carColor: '',
        notes: ''
      }
    case TYPES.SET_ORDERS:
      return { ...state, orders }
    case TYPES.SET_MESSAGE:
      return { ...state, message, messageTimeout, messagePosition, messageColor }
    case TYPES.SET_PROMO_CODE:
      return { ...state, promoCode }
    case TYPES.API_PROMO_CODES_INFO_SUCCESS:
      return { ...state, promoCodeInfo }
    case TYPES.SET_PROMO_CODE_REFUSAL: {
      // A held code that is the one refused comes off: no money off for it.
      const refused = (promoCodeRefusal?.code || '').toLowerCase()
      const same = held => !!refused && (held?.code || '').toString().trim().toLowerCase() === refused
      return {
        ...state,
        promoCodeRefusal,
        promoCodeInfo: same(state.promoCodeInfo) ? null : state.promoCodeInfo,
        promoCode: same(state.promoCode) ? null : state.promoCode
      }
    }
    case TYPES.SET_CREDIT_PREVIEW:
      return { ...state, creditPreviewCents: creditPreviewCents || 0 }
    case TYPES.API_PROMO_CODES_APPLY_SUCCESS:
      const currencyCredit = GBP(credit)

      return { ...state, user: { ...state.user, credit: currencyCredit.format(), credit_cents: currencyCredit.intValue } }
    case TYPES.RESET_BOOKING_FORM:
      const firstBaseService = defaultBaseService(state.prices.ids.map(serviceId => state.prices.byId[serviceId]))
      const firstBaseServiceId = firstBaseService.id
      const largeCarServiceId = state.prices.ids.find(serviceId => state.prices.byId[serviceId].name === 'Extra for large car')
      const deselectedServices = {
        ...state.prices.ids.reduce((acc, serviceId) => ({ ...acc, [serviceId]: false }), {}),
        [firstBaseServiceId]: true,
        [largeCarServiceId]: null
      }
      const resetServicesAvailability = state.prices.ids.reduce(
        (acc, serviceId) => ({ ...acc, [serviceId]: !firstBaseService.includes.includes(serviceId) }),
        {}
      )

      return { ...state, ...initialBookingForm, services: deselectedServices, servicesAvailability: resetServicesAvailability, date: null, promoCode: null }
    case TYPES.SET_IS_CARD_COMPLETE:
      return { ...state, isCardComplete }
    case TYPES.SET_IS_PROCESSING_STRIPE:
      return { ...state, isProcessingStripe }
    case TYPES.SET_IS_STRIPE_ERROR:
      return { ...state, isStripeError }
    case TYPES.SET_STRIPE_ERROR:
      return { ...state, stripeError }
    case TYPES.SET_IS_TERMS_ACCEPTED:
      return { ...state, isTermsAccepted }
    case TYPES.API_GET_CONFIG_SUCCESS:
      return { ...state, config }
    default:
      return state
  }
}
